import { create } from "zustand"
import type { AgentProjectRun } from "@/lib/agent-api"

/**
 * Статусы прогонов ассистентов проекта, приходящие одним общим стримом.
 * Плашки делегирования читают отсюда, поэтому спиннер живёт независимо от того,
 * открыт ли чат целевого ассистента и на какой мы странице.
 */
type AgentRunsState = {
  projectId: number | null
  runs: Record<number, AgentProjectRun>
  /** Снапшот с сервера уже приходил: отсутствие записи = прогон не идёт. */
  hydrated: boolean
  /** Полный снапшот активных прогонов: пропавшие из списка помечаем idle, не удаляем. */
  setRuns: (projectId: number, runs: AgentProjectRun[]) => void
  /** Точечное обновление из cable. */
  mergeRun: (projectId: number, run: AgentProjectRun) => void
  reset: () => void
}

export const useAgentRunsStore = create<AgentRunsState>((set) => ({
  projectId: null,
  runs: {},
  hydrated: false,
  setRuns: (projectId, runs) =>
    set((state) => {
      const incoming: Record<number, AgentProjectRun> = Object.fromEntries(runs.map((r) => [r.conversation_id, r]))
      if (state.projectId === projectId) {
        for (const [id, prev] of Object.entries(state.runs)) {
          const conversationId = Number(id)
          if (!(conversationId in incoming)) {
            incoming[conversationId] = { ...prev, status: "idle" }
          }
        }
      }
      return { projectId, runs: incoming, hydrated: true }
    }),
  mergeRun: (projectId, run) =>
    set((state) => {
      if (state.projectId !== null && state.projectId !== projectId) {
        return { projectId, runs: { [run.conversation_id]: run }, hydrated: false }
      }
      return { projectId, runs: { ...state.runs, [run.conversation_id]: run } }
    }),
  reset: () => set(() => ({ projectId: null, runs: {}, hydrated: false })),
}))

export function selectAgentRun(conversationId: number | null | undefined) {
  return (state: AgentRunsState): AgentProjectRun | null => {
    if (!conversationId) return null
    return state.runs[conversationId] ?? null
  }
}
